/**
 * useDebouncedCallback — Feb 2026 (Latency Fix #4)
 *
 * Returns a debounced version of `fn` that resets its timer on every call and
 * only fires the LAST invocation after `wait` ms of silence. Also returns a
 * `flush` and `cancel` helper.
 *
 * Notes:
 *   - The debounced function is stable (identity doesn't change across
 *     renders) so it's safe to pass to useEffect / useCallback deps.
 *   - Cleans up its timer on unmount to avoid setState-after-unmount warnings.
 *   - Uses a ref-latest pattern for `fn` so the freshest closure is invoked
 *     without requiring callers to memoize `fn`.
 */
import { useCallback, useEffect, useRef } from 'react';

export function useDebouncedCallback(fn, wait = 300) {
  const fnRef = useRef(fn);
  const timerRef = useRef(null);

  // Always call the freshest version — no need for callers to memoize.
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const debounced = useCallback((...args) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      fnRef.current?.(...args);
    }, wait);
  }, [wait]);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const flush = useCallback((...args) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    fnRef.current?.(...args);
  }, []);

  return Object.assign(debounced, { cancel, flush });
}

export default useDebouncedCallback;
